import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useVideoWebSocket } from '../hooks/useWebSocket';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Phone, PhoneOff, Mic, MicOff, Video, VideoOff, 
  MessageCircle, Users, Settings, Maximize, Minimize, Monitor, MonitorOff
} from 'lucide-react';
import { toast } from 'sonner';
import { videoAPI } from '../lib/api';
import usePageTitle from '../hooks/usePageTitle';



const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]
};

const VideoCall = () => {
  usePageTitle('Video Call');
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [roomData, setRoomData] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [isConnecting, setIsConnecting] = useState(true);
  const [participants, setParticipants] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenStream, setScreenStream] = useState(null);
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const containerRef = useRef(null);
  const screenSenderRef = useRef(null);

  // Get auth token for WebSocket from Firebase
  const [token, setToken] = useState(null);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const { auth } = await import('../lib/firebase');
        if (auth.currentUser) {
          const idToken = await auth.currentUser.getIdToken();
          setToken(idToken);
        }
      } catch (e) {
        console.error('Failed to get token:', e);
      }
    };
    fetchToken();
  }, []);

  // WebSocket for signaling
  const ws = useVideoWebSocket(roomId, token, {
    onOffer: async (data) => {
      console.log('Received offer from:', data.sender_id);
      await handleOffer(data.offer, data.sender_id);
    },
    onAnswer: async (data) => {
      console.log('Received answer from:', data.sender_id);
      if (peerConnectionRef.current) {
        await peerConnectionRef.current.setRemoteDescription(data.answer);
      }
    },
    onCandidate: async (data) => {
      console.log('Received ICE candidate');
      if (peerConnectionRef.current && data.candidate) {
        await peerConnectionRef.current.addIceCandidate(data.candidate);
      }
    },
    onUserJoined: (data) => {
      console.log('User joined:', data.user_id);
      setParticipants(prev => [...new Set([...prev, data.user_id])]);
      // Create offer for new user
      if (localStream) {
        createOffer();
      }
    },
    onUserLeft: (data) => {
      console.log('User left:', data.user_id);
      setParticipants(prev => prev.filter(p => p !== data.user_id));
      // Clean up peer connection if remote user left
      if (remoteStream) {
        remoteStream.getTracks().forEach(track => track.stop());
        setRemoteStream(null);
      }
    },
    onMuteStatus: (data) => {
      toast.info(`${data.user_id} ${data.audio_muted ? 'muted' : 'unmuted'} their mic`);
    }
  });

  // Initialize local media
  useEffect(() => {
    const initMedia = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });
        setLocalStream(stream);
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
        setIsConnecting(false);
      } catch (error) {
        console.error('Failed to get media:', error);
        toast.error('Could not access camera/microphone');
        setIsConnecting(false);
      }
    };

    initMedia();

    return () => {
      // Cleanup on unmount
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
    };
  }, []);

  // Fetch room data
  useEffect(() => {
    const fetchRoom = async () => {
      try {
        const res = await videoAPI.getRoom(roomId);
        setRoomData(res.data);
        setParticipants(res.data.participants || []);
      } catch (error) {
        console.error('Failed to fetch room:', error);
        toast.error('Room not found');
        navigate('/dashboard');
      }
    };
    if (roomId) fetchRoom();
  }, [roomId, navigate]);

  // Create peer connection
  const createPeerConnection = useCallback(() => {
    const pc = new RTCPeerConnection(ICE_SERVERS);
    
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        ws.sendCandidate(event.candidate);
      }
    };

    pc.ontrack = (event) => {
      console.log('Received remote track');
      const [stream] = event.streams;
      setRemoteStream(stream);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = stream;
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('ICE state:', pc.iceConnectionState);
    };

    // Add local tracks
    if (localStream) {
      localStream.getTracks().forEach(track => {
        pc.addTrack(track, localStream);
      });
    }

    peerConnectionRef.current = pc;
    return pc;
  }, [localStream, ws]);

  // Create and send offer
  const createOffer = useCallback(async () => {
    const pc = peerConnectionRef.current || createPeerConnection();
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      ws.sendOffer(offer);
    } catch (error) {
      console.error('Failed to create offer:', error);
    }
  }, [createPeerConnection, ws]);

  // Handle incoming offer
  const handleOffer = async (offer, senderId) => {
    const pc = peerConnectionRef.current || createPeerConnection();
    try {
      await pc.setRemoteDescription(offer);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      ws.sendAnswer(answer);
    } catch (error) {
      console.error('Failed to handle offer:', error);
    }
  };

  // Toggle audio
  const toggleAudio = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioMuted(!audioTrack.enabled);
        ws.sendMuteStatus(!audioTrack.enabled, isVideoMuted);
      }
    }
  };

  // Toggle video
  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoMuted(!videoTrack.enabled);
        ws.sendMuteStatus(isAudioMuted, !videoTrack.enabled);
      }
    }
  };

  // End call
  const endCall = () => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
    navigate('/dashboard');
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Toggle screen sharing
  const toggleScreenShare = async () => {
    if (isScreenSharing) {
      // Stop screen sharing
      if (screenStream) {
        screenStream.getTracks().forEach(track => track.stop());
        setScreenStream(null);
      }
      // Restore camera track to peer connection
      if (peerConnectionRef.current && localStream) {
        const videoTrack = localStream.getVideoTracks()[0];
        if (videoTrack && screenSenderRef.current) {
          screenSenderRef.current.replaceTrack(videoTrack);
        }
      }
      setIsScreenSharing(false);
      ws.send?.({ type: 'screen_share_stop' });
    } else {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        setScreenStream(stream);
        setIsScreenSharing(true);

        // Replace video track in peer connection
        const screenTrack = stream.getVideoTracks()[0];
        if (peerConnectionRef.current) {
          const senders = peerConnectionRef.current.getSenders();
          const videoSender = senders.find(s => s.track?.kind === 'video');
          if (videoSender) {
            screenSenderRef.current = videoSender;
            videoSender.replaceTrack(screenTrack);
          }
        }

        ws.send?.({ type: 'screen_share_start' });

        // Handle user stopping via browser UI
        screenTrack.onended = () => {
          toggleScreenShare();
        };
      } catch (error) {
        console.error('Screen share failed:', error);
        toast.error('Could not start screen sharing');
      }
    }
  };

  if (isConnecting) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dark-slate mx-auto mb-4"></div>
            <p className="text-slate-gray">Connecting to video call...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <div ref={containerRef} className="min-h-screen bg-dark-slate">
      {/* Video Grid */}
      <div className="relative h-screen">
        {/* Remote Video (Large) */}
        <div className="absolute inset-0">
          {remoteStream ? (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-slate-900">
              <div className="text-center">
                <Avatar className="w-24 h-24 mx-auto mb-4">
                  <AvatarFallback className="bg-slate-700 text-white text-3xl">
                    ?
                  </AvatarFallback>
                </Avatar>
                <p className="text-white">Waiting for others to join...</p>
              </div>
            </div>
          )}
        </div>

        {/* Local Video (Small, Picture-in-Picture) */}
        <div className="absolute bottom-24 right-4 w-48 h-36 rounded-xl overflow-hidden shadow-lg border-2 border-white/20">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className={`w-full h-full object-cover ${isVideoMuted ? 'opacity-0' : ''}`}
          />
          {isVideoMuted && (
            <div className="absolute inset-0 bg-slate-800 flex items-center justify-center">
              <Avatar className="w-16 h-16">
                <AvatarImage src={user?.profile_photo} />
                <AvatarFallback className="bg-mint-fresh text-dark-slate">
                  {user?.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
            </div>
          )}
        </div>

        {/* Top Bar */}
        <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/50 to-transparent">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 bg-white/10 rounded-full px-3 py-1">
                <Users className="w-4 h-4 text-white" />
                <span className="text-white text-sm">{participants.length}</span>
              </div>
              {ws.isConnected && (
                <div className="flex items-center space-x-2 bg-emerald-500/20 rounded-full px-3 py-1">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                  <span className="text-emerald-400 text-sm">Connected</span>
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleFullscreen}
              className="text-white hover:bg-white/10"
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
          <div className="flex items-center justify-center space-x-4">
            <Button
              onClick={toggleAudio}
              className={`w-14 h-14 rounded-full ${
                isAudioMuted ? 'bg-red-500 hover:bg-red-600' : 'bg-white/20 hover:bg-white/30'
              }`}
              data-testid="toggle-audio"
            >
              {isAudioMuted ? <MicOff className="w-6 h-6 text-white" /> : <Mic className="w-6 h-6 text-white" />}
            </Button>
            
            <Button
              onClick={toggleVideo}
              className={`w-14 h-14 rounded-full ${
                isVideoMuted ? 'bg-red-500 hover:bg-red-600' : 'bg-white/20 hover:bg-white/30'
              }`}
              data-testid="toggle-video"
            >
              {isVideoMuted ? <VideoOff className="w-6 h-6 text-white" /> : <Video className="w-6 h-6 text-white" />}
            </Button>
            
            <Button
              onClick={endCall}
              className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600"
              data-testid="end-call"
            >
              <PhoneOff className="w-7 h-7 text-white" />
            </Button>
            
            <Button
              onClick={toggleScreenShare}
              className={`w-14 h-14 rounded-full ${
                isScreenSharing ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-white/20 hover:bg-white/30'
              }`}
              data-testid="toggle-screen-share"
            >
              {isScreenSharing ? <MonitorOff className="w-6 h-6 text-white" /> : <Monitor className="w-6 h-6 text-white" />}
            </Button>
            
            <Button
              className="w-14 h-14 rounded-full bg-white/20 hover:bg-white/30"
            >
              <MessageCircle className="w-6 h-6 text-white" />
            </Button>
            
            <Button
              className="w-14 h-14 rounded-full bg-white/20 hover:bg-white/30"
            >
              <Settings className="w-6 h-6 text-white" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoCall;
